import api from "./api";

/* ========== Tipos compartidos ========== */
export type CompanyStatus = "ACTIVE" | "INACTIVE";

export type RoleScope =
  | "GLOBAL"
  | "DEVIOZ"
  | "BCP"
  | "BEMBOS"
  | "CLARO"
  | "PLAZAVEA"
  | "GLORIA";

export type Company = {
  id: number;
  name: string;
  domain: string;
  slug: string;
  status: CompanyStatus;
  created_at?: string | null;
};

export type Role = {
  id: number;
  code: string;
  name: string;
  scope: RoleScope;
  created_at?: string | null;
};

export type UserRow = {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  company_id: number;
  company_name: string | null;
  roles: string[];
};

export type Page<T> = {
  data: T[];
  pagination: { page: number; pageSize: number; total: number; pages: number };
};

/* ===== Helpers para no romper si backend devuelve array o Page ===== */
function toPage<T>(res: any, page = 1, pageSize = 20): Page<T> {
  if (res?.data && res?.pagination) return res as Page<T>;
  if (Array.isArray(res)) {
    return {
      data: res as T[],
      pagination: {
        page,
        pageSize,
        total: res.length,
        pages: 1,
      },
    };
  }
  if (Array.isArray(res?.data) && res?.pagination) return res as Page<T>;
  // fallback ultra seguro
  const arr = Array.isArray(res?.data) ? res.data : [];
  return {
    data: arr,
    pagination: { page, pageSize, total: arr.length, pages: 1 },
  };
}

/* ========== Dashboard ========== */
export type MetricsResponse = {
  range: { from: string | null; to: string | null };
  totals: { users: number; roles: number; companies: number; costs: number };
};

export type CostsDailyPoint = { date: string; amount: number };

export type CompanySummaryItem = {
  id: number;
  name: string;
  domain: string;
  slug: string;
  users: number;
  costs: number;
};

export type SalesByCompany = { id: number; name: string; slug: string; amount: number };

export async function getDeviozMetrics(params?: {
  from?: string;
  to?: string;
  month?: string;
  year?: string;
}): Promise<MetricsResponse> {
  return (await api.get("/devioz/dashboard/metrics", { params })) as MetricsResponse;
}

export async function getCostsDaily(params: {
  from?: string;
  to?: string;
  month?: string;
  year?: string;
}): Promise<CostsDailyPoint[]> {
  return (await api.get("/devioz/dashboard/costs/daily", { params })) as CostsDailyPoint[];
}

export async function getCompaniesSummary(params?: {
  from?: string;
  to?: string;
  month?: string;
  year?: string;
}): Promise<{ range: { from: string | null; to: string | null }; data: CompanySummaryItem[] }> {
  return (await api.get("/devioz/dashboard/companies/summary", { params })) as {
    range: { from: string | null; to: string | null };
    data: CompanySummaryItem[];
  };
}

export async function getSalesByCompany(params?: {
  from?: string;
  to?: string;
  month?: string;
  year?: string;
}): Promise<SalesByCompany[]> {
  return (await api.get("/devioz/dashboard/sales/by-company", { params })) as SalesByCompany[];
}

/* ========== Companies ========== */
export async function listCompanies(params?: {
  q?: string;
  status?: CompanyStatus;
  page?: number;
  pageSize?: number;
}): Promise<Page<Company>> {
  const p = params?.page ?? 1;
  const ps = params?.pageSize ?? 200;
  const res = await api.get("/devioz/companies", { params });
  return toPage<Company>(res, p, ps);
}

export async function createCompany(body: {
  name: string;
  domain: string;
  slug: string;
}): Promise<Company> {
  return (await api.post("/devioz/companies", body)) as Company;
}

export async function updateCompany(
  id: number,
  body: Partial<Pick<Company, "name" | "domain" | "slug" | "status">>
): Promise<Company> {
  return (await api.put(`/devioz/companies/${id}`, body)) as Company;
}

export async function setCompanyStatus(
  id: number,
  status: CompanyStatus
): Promise<{ id: number; status: CompanyStatus }> {
  return (await api.patch(`/devioz/companies/${id}/status`, { status })) as {
    id: number;
    status: CompanyStatus;
  };
}

export async function deleteCompany(
  id: number
): Promise<{ id: number; status?: CompanyStatus; deleted?: boolean }> {
  return (await api.delete(`/devioz/companies/${id}`)) as {
    id: number;
    status?: CompanyStatus;
    deleted?: boolean;
  };
}

/* ========== Roles ========== */
export async function listRoles(params?: {
  q?: string;
  scope?: RoleScope;
  page?: number;
  pageSize?: number;
}): Promise<Page<Role>> {
  const p = params?.page ?? 1;
  const ps = params?.pageSize ?? 200;
  const res = await api.get("/devioz/roles", { params });
  return toPage<Role>(res, p, ps);
}

export async function createRole(body: {
  code: string;
  name: string;
  scope: RoleScope;
}): Promise<Role> {
  return (await api.post("/devioz/roles", body)) as Role;
}

export async function updateRole(
  id: number,
  body: Partial<Pick<Role, "code" | "name" | "scope">>
): Promise<Role> {
  return (await api.put(`/devioz/roles/${id}`, body)) as Role;
}

export async function deleteRole(id: number): Promise<{ id: number; deleted: boolean }> {
  return (await api.delete(`/devioz/roles/${id}`)) as { id: number; deleted: boolean };
}

/* ========== Users ========== */
export async function listUsers(params?: {
  q?: string;
  company_id?: number;
  role?: string;
  page?: number;
  pageSize?: number;
}): Promise<Page<UserRow>> {
  const p = params?.page ?? 1;
  const ps = params?.pageSize ?? 20;
  const res = await api.get("/devioz/users", { params });
  return toPage<UserRow>(res, p, ps);
}

export async function createUser(body: {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  company_id?: number;
  roles?: string[];
}): Promise<UserRow> {
  return (await api.post("/devioz/users", body)) as UserRow;
}

export async function updateUser(
  id: number,
  body: Partial<{ first_name: string; last_name: string; email: string; company_id: number }>
): Promise<UserRow> {
  return (await api.put(`/devioz/users/${id}`, body)) as UserRow;
}

export async function updateUserPassword(
  id: number,
  password: string
): Promise<{ id: number; updated: boolean }> {
  return (await api.patch(`/devioz/users/${id}/password`, { password })) as {
    id: number;
    updated: boolean;
  };
}

export async function setUserRoles(
  id: number,
  roles: string[]
): Promise<{ id: number; roles: string[] }> {
  return (await api.put(`/devioz/users/${id}/roles`, { roles })) as {
    id: number;
    roles: string[];
  };
}

export async function deleteUser(id: number): Promise<{ id: number; deleted: boolean }> {
  return (await api.delete(`/devioz/users/${id}`)) as { id: number; deleted: boolean };
}
